import axios from "axios";
import { getUserDataFromToken } from "../utils/AuthUtils";

const APP_ID = process.env.REACT_APP_ONESIGNAL_APP_ID;
const BASE_URL = `https://onesignal.com/api/v1`;

const headers = {
  "Content-Type": "application/json",
};

export class OneSignalService {
  static async createUser({ _id, email }) {
    try {
      const res = await axios.post(
        `${BASE_URL}/apps/${APP_ID}/users`,
        {
          identity: {
            external_id: _id,
          },
          subscription: [
            {
              type: "Email",
              token: email,
            },
          ],
        },
        { headers }
      );
      return res.data;
    } catch (err) {
      console.error(
        "OneSignal createUser error",
        err.response?.data || err.message
      );
      return null;
    }
  }

  static async updateUser({ data, oneSignalId }) {
    try {
      if (!oneSignalId) {
        oneSignalId = await this.getOneSignalIdByExternalId();
      }

      if (!oneSignalId) {
        throw new Error("OneSignal ID not found");
      }

      const res = await axios.patch(
        `${BASE_URL}/apps/${APP_ID}/users/by/onesignal_id/${oneSignalId}`,
        data,
        { headers }
      );

      console.log("Response received with method:", res.config.method);

      return res.data;
    } catch (err) {
      console.error(
        "OneSignal updateUser error",
        err.response?.data || err.message
      );
      return null;
    }
  }

  static async createEmailSubscription({ oneSignalId, email }) {
    try {
      if (!oneSignalId) {
        oneSignalId = await this.getOneSignalIdByExternalId();
      }
      const res = await axios.post(
        `${BASE_URL}/apps/${APP_ID}/users/by/onesignal_id/${oneSignalId}/subscriptions`,
        {
          subscription: {
            type: "Email",
            token: email,
          },
        },
        { headers }
      );

      return res.data;
    } catch (err) {
      console.error(
        "OneSignal createEmailSubscription error",
        err.response?.data || err.message
      );
      return null;
    }
  }

  static async updateSubscription(
    subscriptionId,
    options = { enabled: true, notification_types: 1 }
  ) {
    try {
      const res = await axios.patch(
        `${BASE_URL}/apps/${APP_ID}/subscriptions/${subscriptionId}`,
        options,
        { headers }
      );

      return res.data;
    } catch (err) {
      console.error(
        "OneSignal updateSubscription error",
        err.response?.data || err.message
      );
      return null;
    }
  }

  // static async getUser(oneSignalId) {
  static async getOneSignalIdByExternalId() {
    const { _id, email } = getUserDataFromToken();
    const data = await this.createUser({
      _id,
      email,
    });

    return data?.identity?.onesignal_id;
  }
  static async getUser(oneSignalId) {
    try {
      const res = await axios.get(
        `${BASE_URL}/apps/${APP_ID}/users/by/onesignal_id/${oneSignalId}`
      );

      return res.data;
    } catch (err) {
      console.error(
        "OneSignal getUser error",
        err.response?.data || err.message
      );
      return null;
    }
  }

  static async registerUser(user) {
    try {
      if (!user) return;
      const { _id, email, role, firstName, lastName, phone, restaurant } = user;
      const result = await this.createUser(user);
      const userOneSignalId = result?.identity?.onesignal_id;
      const subscriptionResult = await this.createEmailSubscription({
        email,
        oneSignalId: userOneSignalId,
      });
      await this.updateUser({
        oneSignalId: userOneSignalId,
        data: {
          properties: {
            email,
            tags: {
              user_email: email,
              user_first_name: firstName,
              user_last_name: lastName,
              user_phone: phone,
              restaurant_id: restaurant,
              user_role: role,
              user_id: _id,
            },
          },
        },
      });

      const subscriptionId = subscriptionResult?.subscription?.id;

      if (subscriptionId)
        await this.updateSubscription(subscriptionId, {
          enabled: true,
          notification_types: 1,
        });
    } catch (error) {
      console.error("OneSignal registerUser error", error);
      return null;
    }
  }

  static async updateUserLocation(location) {
    try {
      if (!location) return;
      const { street, region, country, state, city, restaurant_location } =
        location;
      return await this.updateUser({
        data: {
          properties: {
            tags: {
              restaurant_region: region || "null",
              restaurant_address: street,
              restaurant_country: country,
              restaurant_state: state,
              restaurant_city: city,
              restaurant_location: restaurant_location
                ? JSON.stringify(restaurant_location)
                : null,
            },
          },
        },
      });
    } catch (error) {
      console.error("OneSignal updateUserLocation error", error);
      return null;
    }
  }

  static async updateRestaurantName(restaurantName) {
    try {
      if (!restaurantName) return;
      return await this.updateUser({
        data: {
          properties: {
            tags: {
              restaurant_name: restaurantName,
            },
          },
        },
      });
    } catch (error) {
      console.error("OneSignal updateRestaurantName error", error);
      return null;
    }
  }
}
